import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Button, Menu, MenuItem } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useState } from "react";
import { AddBillOfLadingButton } from "routes/Customer/Waste/ShippingDocuments/AddBillOfLadingButton";

import { AddManifestButton } from "./AddManifestButton";

/** Which record's upload flow the menu opened, if any. */
type OpenFlow = "billOfLading" | "manifest" | null;

/**
 * The merged Manifests list's add control: one "Add Shipping Record" button
 * whose dropdown chooses which kind of record to add.
 *
 * The dropdown is only offered when both flows are actually available to this
 * user. Manifest upload needs `canQueueManifestExtraction`; bill-of-lading
 * upload needs the resolved `ContainerTracking` flag — the same two gates the
 * child buttons apply to themselves, read here so the control's shape and the
 * children can't disagree. With one flow available the single flow's own button
 * is rendered, named for what it does, rather than a menu whose other choice
 * would do nothing; with neither, nothing renders at all.
 *
 * In the dropdown case both children are rendered with their `open` prop
 * given, so each contributes its upload + review dialogs while this component
 * supplies the only visible trigger. Their lifetime is this component's, not
 * the menu's, so closing the menu on a choice doesn't tear the flow down.
 *
 * `onSaved` reaches the manifest child only. A bill of lading's apply mutation
 * refetches the merged list's query itself (`useApplyReviewedBillOfLading`), so
 * routing that side through the host too would issue the same query twice.
 */
export function AddShippingRecordButton({
  tenantId,
  onSaved,
}: {
  tenantId: string;
  /** Called once an added manifest's review is applied, so a host can refetch. */
  onSaved?: () => void;
}) {
  const { canQueueManifestExtraction } = useQueueManifestDocumentExtraction();
  const { featureFlags } = useFeatureFlags();
  const canAddBillOfLading = !!featureFlags[FeatureFlagKey.ContainerTracking];

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openFlow, setOpenFlow] = useState<OpenFlow>(null);

  if (!canQueueManifestExtraction && !canAddBillOfLading) {
    return null;
  }

  if (!canQueueManifestExtraction) {
    return <AddBillOfLadingButton tenantId={tenantId} />;
  }

  if (!canAddBillOfLading) {
    return <AddManifestButton tenantId={tenantId} onSaved={onSaved} />;
  }

  const choose = (flow: OpenFlow) => {
    setAnchorEl(null);
    setOpenFlow(flow);
  };

  // A child closing its own upload dialog clears the selection, so the same
  // choice can be made again.
  const trackOpen = (flow: OpenFlow) => (open: boolean) => setOpenFlow(open ? flow : null);

  return (
    <>
      <Button
        variant="contained"
        endIcon={<ArrowDropDown />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Add Shipping Record
      </Button>
      <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => choose("billOfLading")}>Bill of Lading</MenuItem>
        <MenuItem onClick={() => choose("manifest")}>Manifest</MenuItem>
      </Menu>
      <AddBillOfLadingButton
        tenantId={tenantId}
        open={openFlow === "billOfLading"}
        onOpenChange={trackOpen("billOfLading")}
      />
      <AddManifestButton
        tenantId={tenantId}
        onSaved={onSaved}
        open={openFlow === "manifest"}
        onOpenChange={trackOpen("manifest")}
      />
    </>
  );
}
